// =============================================================================
// DocumentoOrcamento — o orçamento no formato que a loja já conhece.
//
// Réplica do impresso do Microuniverso, o mesmo que o Portal Logística mostra
// no quadro de rotas. A semelhança NÃO é enfeite: no dia em que o ERP cai,
// ninguém aprende layout novo. O vendedor reconhece o papel, o cliente recebe
// o documento de sempre, e o treinamento acontece por osmose.
//
// O MESMO componente serve três situações, e é de propósito: uma tela só não
// tem como divergir de si mesma na primeira mudança.
//
//   sem `edicao`  → documento de leitura (pedido salvo, impressão)
//   com `edicao`  → o documento É o formulário: cada decisão do vendedor vira
//                   campo no lugar onde ela aparece no papel
//
// ⚠️ Nem todo campo do papel é preenchível, e confundir isso criaria divergência
//    com o ERP. Vêm de fora e ficam como texto:
//      · nº do orçamento e situação — do ERP na integração / do estado
//      · cód., descrição e unidade do produto — do catálogo espelhado
//      · CNPJ, cód. e o bloco ENDEREÇO — do cadastro do cliente no ERP
//      · subtotal, mercadorias e total — calculados
//      · vendedor — do crachá; troca por senha, não se digita
//    Editável é o que é decisão de quem vende.
// =============================================================================

import { useEffect, useRef, useState } from 'react';
import api from '../services/api';
import HistoricoCliente from './HistoricoCliente';
import logo from '../assets/logo-centrao.png';

// Campo numerico que ja vem com "0" faz o vendedor digitar "50" e ver "050".
// Selecionar ao focar resolve sem mascara nem normalizacao no meio da digitacao.
const selecionarTudo = (e) => e.target.select();

const dinheiro = (v) => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const qtd = (v) => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
const numero = (v) => Number(v || 0).toLocaleString('pt-BR', { maximumFractionDigits: 4 });
const data = (v) => (v ? String(v).slice(0, 10).split('-').reverse().join('/') : '—');

function docFormatado(v) {
  const d = String(v || '').replace(/\D/g, '');
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  return v || '—';
}

const soDigitosDoc = (v) => String(v || '').replace(/\D/g, '');

// Mascara enquanto digita: o vendedor le o documento no formato do papel, e a
// troca de CPF para CNPJ acontece sozinha no 12o digito.
function mascaraDoc(v) {
  const d = soDigitosDoc(v).slice(0, 14);
  if (d.length <= 11) {
    return d.replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }
  return d.replace(/^(\d{2})(\d)/, '$1.$2')
          .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
          .replace(/\.(\d{3})(\d)/, '.$1/$2')
          .replace(/(\d{4})(\d)/, '$1-$2');
}

// Digito verificador de CPF e CNPJ. Nao e purismo: erro de digitacao aqui vira
// cliente duplicado no Microuniverso, e la ninguem conserta sem apagar historico.
function cpfValido(d) {
  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;
  const dv = (ate) => {
    let soma = 0;
    for (let i = 0; i < ate; i++) soma += Number(d[i]) * (ate + 1 - i);
    return ((soma * 10) % 11) % 10;
  };
  return dv(9) === Number(d[9]) && dv(10) === Number(d[10]);
}

function cnpjValido(d) {
  if (d.length !== 14 || /^(\d)\1{13}$/.test(d)) return false;
  const dv = (base) => {
    let peso = base.length - 7;
    let soma = 0;
    for (let i = 0; i < base.length; i++) {
      soma += Number(base[i]) * peso--;
      if (peso < 2) peso = 9;
    }
    const r = soma % 11;
    return r < 2 ? 0 : 11 - r;
  };
  return dv(d.slice(0, 12)) === Number(d[12]) && dv(d.slice(0, 13)) === Number(d[13]);
}

const documentoValido = (d) => (d.length === 11 ? cpfValido(d) : d.length === 14 ? cnpjValido(d) : false);

// Na leitura, o documento mostra o NOME, não o código. O código virou o OID do
// ERP com a migration 015 — "2333042" no papel não diz nada a ninguém.
function nomeDe(lista, codigo) {
  if (!codigo) return null;
  const achado = (lista || []).find((x) => String(x.codigo) === String(codigo));
  return achado ? achado.nome : codigo;
}

function cepFormatado(v) {
  const d = String(v || '').replace(/\D/g, '');
  return d.length === 8 ? d.replace(/(\d{5})(\d{3})/, '$1-$2') : (v || '—');
}

// O documento tem TRES estados, e cada um exige uma frase diferente. Confundi-los
// custa telefonema: dizer "enviando" numa previa faz o vendedor
// procurar um pedido que ainda nem foi salvo; mostrar numero do Fenix sem avisar
// faz alguem procura-lo no Microuniverso e nao achar.
function estadoDoDocumento(pedido, previa) {
  if (previa) {
    return {
      titulo: 'Orçamento',
      situacao: 'Em digitação — ainda não confirmado',
      // O rascunho grava sozinho (a faixa de cima diz "Rascunho salvo"); o que
      // ainda não aconteceu é ir ao ERP. Dizer "nada foi gravado" contradizia a faixa.
      aviso: 'Ainda não foi para o Microuniverso: vai quando você confirmar.',
    };
  }
  if (pedido.numped_erp) {
    return { titulo: `Orçamento: ${pedido.numped_erp}`, situacao: 'No Microuniverso', aviso: null };
  }
  if (pedido.status === 'RASCUNHO') {
    return { titulo: `Rascunho: ${pedido.numero}`, situacao: 'Em digitação — ainda não confirmado', aviso: 'Ainda não foi ao Microuniverso.' };
  }
  return {
    titulo: `Pedido: ${pedido.numero}`,
    situacao: pedido.status === 'DIVERGENTE' ? 'Sem resposta do Microuniverso' : 'Enviando ao Microuniverso',
    aviso: `Ainda sem número no Microuniverso — registrado no Fênix como ${pedido.numero}.`,
  };
}

// A foto ampliada. Produto de construcao se confere pela imagem — a miniatura
// de 34px diz que tem foto, nao qual e o produto.
function FotoLupa({ foto, onFechar }) {
  useEffect(() => {
    const tecla = (e) => { if (e.key === 'Escape') onFechar(); };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [onFechar]);

  return (
    <div className="foto-lupa nao-imprimir" onClick={onFechar} role="dialog" aria-label={foto.descricao}>
      <button type="button" className="foto-lupa__fechar" aria-label="Fechar">
        <i className="bi bi-x-lg" />
      </button>
      <img src={foto.url} alt={foto.descricao}
           onClick={(e) => e.stopPropagation()} />
      <div className="foto-lupa__legenda">
        {foto.descricao} · cód. {foto.codpro}
      </div>
    </div>
  );
}

function SecaoBar({ titulo, acao }) {
  return (
    <div className="erp-doc__secao-bar">
      {titulo}
      {acao && <span className="erp-doc__secao-acao nao-imprimir">{acao}</span>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Busca de cliente, dentro do proprio bloco CLIENTE do documento.
// ---------------------------------------------------------------------------
function BuscaCliente({ aoEscolher }) {
  const [termo, setTermo] = useState('');
  const [achados, setAchados] = useState([]);

  useEffect(() => {
    if (termo.trim().length < 3) { setAchados([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data: d } = await api.get('/api/clientes', { params: { busca: termo.trim() } });
        setAchados([...d.clientes, ...d.rascunhos.map((r) => ({ ...r, rascunho: true }))]);
      } catch { setAchados([]); }
    }, 320);
    return () => clearTimeout(t);
  }, [termo]);

  return (
    <div className="erp-doc__busca">
      <input className="erp-doc__inp erp-doc__inp--larga" autoFocus
             placeholder="Nome, CPF/CNPJ ou código do cliente"
             value={termo} onChange={(e) => setTermo(e.target.value)} />
      {achados.length > 0 && (
        <div className="erp-doc__resultados">
          {achados.map((c) => (
            <div key={c.codclie || `r${c.id}`} className="erp-doc__resultado"
                 onClick={() => { aoEscolher(c); setTermo(''); setAchados([]); }}>
              <div className="fw-semibold">{c.razao}</div>
              <div className="erp-doc__resultado-sub">
                {c.rascunho ? 'cadastro novo — ainda não está no ERP' : `cód. ${c.codclie}`}
                {c.cidade ? ` · ${c.cidade}` : ''}
                {c.bloqueado ? ' · BLOQUEADO' : ''}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ficha do cliente — cadastro novo, ou endereço novo para quem já é cliente.
//
// Mora dentro do bloco CLIENTE do documento. O vendedor nao sai da venda para
// cadastrar, e o cadastro vai ao Microuniverso NA HORA, pela API de venda.
//
// ⚠️ COMECA PELO CPF/CNPJ, e o resto so abre depois dele. O documento e a
//    IDENTIDADE do cliente: se ja existe, ele vai direto para o orcamento, sem
//    digitar nada. Perguntar o nome primeiro fazia o vendedor digitar a ficha
//    inteira para descobrir no fim que o cliente ja era cadastrado.
//
// ⚠️ A API so ACRESCENTA endereco a quem ja e cliente (nao corrige nome nem
//    documento, nem substitui endereco). Por isso o modo "endereco" mostra o
//    nome travado e os campos de endereco em branco. Decisao do Cleiton,
//    30/10/2026: correcao de nome/documento e no Microuniverso.
// ---------------------------------------------------------------------------
function FichaCliente({ cliente, aoSalvar, aoCancelar, aoEncontrar, salvando, erro }) {
  const modoEndereco = !!cliente?.codclie;
  const [cep, setCep] = useState({ estado: 'ocioso', aviso: '' });
  const refNumero = useRef(null);
  const refRazao = useRef(null);
  const ultimoCep = useRef('');
  // Campo que o VENDEDOR digitou nesta ficha — o CEP nao passa por cima dele.
  const digitados = useRef(new Set());
  const vindosDoCep = useRef(new Set());
  const ultimoDoc = useRef('');
  const [doc, setDoc] = useState(modoEndereco ? { estado: 'livre', aviso: '' } : { estado: 'curto', aviso: '' });
  const [f, setF] = useState({
    cpf_cnpj: cliente?.cpf_cnpj ? docFormatado(cliente.cpf_cnpj) : '',
    razao: cliente?.razao || '',
    inscricao_estadual: '', data_nascimento: '',
    telefone: '', celular: '', email: '',
    cep: '', logradouro: '', numero: '', complemento: '', bairro: '', cidade: '', uf: '',
  });
  const campo = (k) => ({
    value: f[k],
    onChange: (e) => { digitados.current.add(k); setF({ ...f, [k]: e.target.value }); },
  });
  const pj = soDigitosDoc(f.cpf_cnpj).length === 14;
  const liberado = modoEndereco || doc.estado === 'livre';

  // O documento e a primeira pergunta. Ja e cliente? Vai para o orcamento.
  // ⚠️ Isto NAO substitui a trava do backend (409); aqui e gentileza.
  useEffect(() => {
    if (modoEndereco) return;
    const d = soDigitosDoc(f.cpf_cnpj);
    if (d.length !== 11 && d.length !== 14) {
      ultimoDoc.current = '';
      setDoc({ estado: 'curto', aviso: '' });
      return;
    }
    if (!documentoValido(d)) {
      ultimoDoc.current = '';
      setDoc({ estado: 'invalido', aviso: d.length === 11 ? 'CPF inválido — confira os números.' : 'CNPJ inválido — confira os números.' });
      return;
    }
    if (d === ultimoDoc.current) return;

    let cancelado = false;
    setDoc({ estado: 'buscando', aviso: '' });
    // Espera a digitacao parar: um CNPJ passa por 11 digitos no caminho.
    const t = setTimeout(async () => {
      if (cancelado) return;
      ultimoDoc.current = d;
      const seguir = (aviso) => {
        if (cancelado) return;
        setDoc({ estado: 'livre', aviso });
        setTimeout(() => refRazao.current?.focus(), 0);
      };
      try {
        const { data } = await api.get('/api/clientes', { params: { busca: d } });
        if (cancelado) return;
        const noErp = (data.clientes || []).find((c) => soDigitosDoc(c.cpf_cnpj) === d);
        if (noErp) {
          setDoc({ estado: 'achado', aviso: '' });
          aoEncontrar?.(noErp);
          return;
        }
        seguir('');
      } catch {
        seguir('Não deu para conferir se esse CPF/CNPJ já é cliente. Pode seguir — se já existir, o sistema avisa ao salvar.');
      }
    }, 350);
    return () => { cancelado = true; clearTimeout(t); };
  }, [f.cpf_cnpj, modoEndereco, aoEncontrar]);

  // O endereco comeca pelo CEP: 8 numeros e o resto se preenche; sobra o
  // numero da casa. CONVENIENCIA, nunca obrigacao — todo campo continua editavel.
  useEffect(() => {
    const digitos = String(f.cep || '').replace(/\D/g, '');
    if (digitos.length !== 8) { setCep({ estado: 'ocioso', aviso: '' }); return; }
    if (digitos === ultimoCep.current) return;
    ultimoCep.current = digitos;

    let cancelado = false;
    setCep({ estado: 'buscando', aviso: '' });
    (async () => {
      try {
        const { data } = await api.get(`/api/clientes/cep/${digitos}`);
        if (cancelado) return;
        const e = data.endereco;
        // O que o vendedor digitou fica; o que veio de um CEP anterior, o CEP novo troca.
        const manter = (k) => digitados.current.has(k) && !vindosDoCep.current.has(k);
        const doCep = { logradouro: e.logradouro, bairro: e.bairro, cidade: e.cidade, uf: e.uf };
        setF((atual) => {
          const novo = { ...atual };
          for (const [k, valor] of Object.entries(doCep)) {
            if (manter(k) || !valor) continue;
            novo[k] = valor;
            vindosDoCep.current.add(k);
          }
          return novo;
        });
        setCep({ estado: 'ok', aviso: '' });
        setTimeout(() => refNumero.current?.focus(), 0);   // o que falta e o numero
      } catch (err) {
        if (cancelado) return;
        setCep({
          estado: 'falhou',
          aviso: err.response?.status === 404
            ? 'CEP não encontrado. Confira o número ou preencha o endereço à mão.'
            : 'Não deu para consultar o CEP. Preencha o endereço à mão.',
        });
      }
    })();
    return () => { cancelado = true; };
  }, [f.cep]);

  return (
    <div className="erp-doc__ficha">
      <div className="erp-doc__ficha-campos">
        <label className="erp-doc__ficha--larga">
          <span>
            CPF / CNPJ
            {doc.estado === 'buscando' && (
              <span className="erp-doc__ficha-dica"><span className="spinner-border spinner-border-sm" /> conferindo…</span>
            )}
            {doc.estado === 'achado' && (
              <span className="erp-doc__ficha-dica"><span className="spinner-border spinner-border-sm" /> já é cliente…</span>
            )}
            {!modoEndereco && doc.estado === 'livre' && (
              <span className="erp-doc__ficha-dica erp-doc__ficha-dica--ok"><i className="bi bi-check-lg" /> cliente novo</span>
            )}
          </span>
          <input className="erp-doc__inp" maxLength={18} inputMode="numeric"
                 placeholder="000.000.000-00" autoFocus={!modoEndereco}
                 disabled={modoEndereco} value={f.cpf_cnpj}
                 onChange={(e) => setF({ ...f, cpf_cnpj: mascaraDoc(e.target.value) })} />
        </label>
        <label className="erp-doc__ficha--larga">
          <span>Nome / Razão social</span>
          <input className="erp-doc__inp" maxLength={100} ref={refRazao}
                 disabled={!liberado || modoEndereco} {...campo('razao')} />
        </label>
        {/* O Microuniverso recusa pessoa física sem data de nascimento (30/10/2026).
            No endereço novo, a data que o ERP já tiver vale — o campo é para quando não tiver. */}
        {!pj && (
          <label style={{ minWidth: 150 }}>
            <span>Nascimento {modoEndereco && <span className="erp-doc__ficha-dica">se o cadastro não tiver</span>}</span>
            <input className="erp-doc__inp" type="date" disabled={!liberado} {...campo('data_nascimento')} />
          </label>
        )}
        {pj && !modoEndereco && (
          <label>
            <span>Inscrição estadual</span>
            <input className="erp-doc__inp" maxLength={20} disabled={!liberado} {...campo('inscricao_estadual')} />
          </label>
        )}
        <label style={{ minWidth: 120 }}>
          <span>
            CEP
            {cep.estado === 'buscando' && (
              <span className="erp-doc__ficha-dica"><span className="spinner-border spinner-border-sm" /> buscando…</span>
            )}
            {cep.estado === 'ok' && (
              <span className="erp-doc__ficha-dica erp-doc__ficha-dica--ok"><i className="bi bi-check-lg" /> encontrado</span>
            )}
          </span>
          <input className="erp-doc__inp" maxLength={9} inputMode="numeric" disabled={!liberado}
                 placeholder="00000-000" autoFocus={modoEndereco} {...campo('cep')} />
        </label>
        <label className="erp-doc__ficha--larga">
          <span>Rua</span>
          <input className="erp-doc__inp" maxLength={120} disabled={!liberado} {...campo('logradouro')} />
        </label>
        <label style={{ minWidth: 80 }}>
          <span>Número <span className="erp-doc__ficha-dica">0 = sem número</span></span>
          <input className="erp-doc__inp" maxLength={6} inputMode="numeric" ref={refNumero}
                 disabled={!liberado} {...campo('numero')} />
        </label>
        <label>
          <span>Complemento</span>
          <input className="erp-doc__inp" maxLength={60} disabled={!liberado} {...campo('complemento')} />
        </label>
        <label>
          <span>Bairro</span>
          <input className="erp-doc__inp" maxLength={80} disabled={!liberado} {...campo('bairro')} />
        </label>
        <label>
          <span>Cidade</span>
          <input className="erp-doc__inp" maxLength={80} disabled={!liberado} {...campo('cidade')} />
        </label>
        <label style={{ minWidth: 70 }}>
          <span>UF</span>
          <input className="erp-doc__inp" maxLength={2} disabled={!liberado} {...campo('uf')} />
        </label>
        <label>
          <span>Telefone</span>
          <input className="erp-doc__inp" maxLength={20} disabled={!liberado} {...campo('telefone')} />
        </label>
        <label>
          <span>Celular</span>
          <input className="erp-doc__inp" maxLength={20} disabled={!liberado} {...campo('celular')} />
        </label>
        <label className="erp-doc__ficha--larga">
          <span>E-mail</span>
          <input className="erp-doc__inp" type="email" maxLength={160} disabled={!liberado} {...campo('email')} />
        </label>
      </div>

      {doc.aviso && (
        <div className="erp-doc__msg erp-doc__msg--aviso">
          <i className="bi bi-exclamation-triangle-fill" /><span>{doc.aviso}</span>
        </div>
      )}
      {cep.aviso && (
        <div className="erp-doc__msg erp-doc__msg--aviso">
          <i className="bi bi-exclamation-triangle-fill" /><span>{cep.aviso}</span>
        </div>
      )}
      {erro && (
        <div className="erp-doc__msg erp-doc__msg--erro">
          <i className="bi bi-x-circle-fill" /><span>{erro}</span>
        </div>
      )}

      <div className="erp-doc__ficha-rodape">
        <span className="erp-doc__resultado-sub">
          <i className="bi bi-info-circle me-1" />
          {!liberado
            ? 'Comece pelo CPF/CNPJ: se o cliente já for cadastrado, ele entra direto no orçamento.'
            : modoEndereco
              ? 'O endereço vai agora para o Microuniverso e passa a ser o deste orçamento. Os endereços que ele já tem continuam lá. '
                + 'Telefone e e-mail em branco não mudam o cadastro. Nome e documento se corrigem no Microuniverso.'
              : 'O cliente é cadastrado agora no Microuniverso.'}
        </span>
        <span>
          <button type="button" className="btn btn-sm btn-outline-secondary me-2"
                  onClick={aoCancelar} disabled={salvando}>
            Cancelar
          </button>
          <button type="button" className="btn btn-sm btn-primary-fnx"
                  onClick={() => aoSalvar(f)} disabled={salvando || !liberado}>
            {salvando
              ? (<><span className="spinner-border spinner-border-sm me-1" />Enviando ao Microuniverso…</>)
              : (modoEndereco ? 'Adicionar endereço' : 'Cadastrar cliente')}
          </button>
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Busca de produto, embutida como ultima linha da tabela de PRODUTOS — que e
// exatamente onde o proximo item vai aparecer.
// ---------------------------------------------------------------------------
function BuscaProduto({ filial, aoEscolher, aoMedirEstoque, aoAmpliar }) {
  const [termo, setTermo] = useState('');
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (termo.trim().length < 2) { setResultados([]); return; }
    timer.current = setTimeout(async () => {
      setBuscando(true);
      try {
        const { data: d } = await api.get('/api/catalogo/produtos', {
          params: { busca: termo.trim(), filial, limite: 20 },
        });
        setResultados(d.produtos);
        if (aoMedirEstoque) aoMedirEstoque(d.estoque);
      } catch { setResultados([]); } finally { setBuscando(false); }
    }, 320);
    return () => timer.current && clearTimeout(timer.current);
  }, [termo, filial, aoMedirEstoque]);

  return (
    <div className="erp-doc__busca">
      <div className="erp-doc__busca-campo">
        <i className="bi bi-plus-circle" />
        <input className="erp-doc__inp erp-doc__inp--larga"
               placeholder="Adicionar produto — código ou descrição (ex.: 30805 ou cimento)"
               value={termo} onChange={(e) => setTermo(e.target.value)} />
        {buscando && <span className="spinner-border spinner-border-sm text-muted" />}
      </div>

      {resultados.length > 0 && (
        <div className="erp-doc__resultados">
          {resultados.map((p) => {
            const naFilial = Number(p.disponivel_na_filial);
            const total = Number(p.disponivel_total);
            return (
              <div key={p.codpro} className="erp-doc__resultado erp-doc__resultado--produto"
                   onClick={() => { aoEscolher(p); setTermo(''); setResultados([]); }}>
                {p.foto
                  ? <img src={p.foto} alt="" loading="lazy"
                         className="erp-doc__resultado-foto erp-doc__foto--clicavel"
                         title="Ver a foto ampliada"
                         onClick={(e) => {
                           // O clique na foto e para CONFERIR, nao para escolher:
                           // sem isso o produto entraria no orcamento junto.
                           e.stopPropagation();
                           aoAmpliar({ url: p.foto, descricao: p.descricao, codpro: p.codpro });
                         }}
                         onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
                  : <span className="erp-doc__resultado-foto erp-doc__resultado-foto--vazia"><i className="bi bi-box" /></span>}
                <div className="flex-grow-1 min-width-0">
                  <div className="fw-semibold text-truncate">{p.descricao}</div>
                  <div className="erp-doc__resultado-sub">
                    cód. {p.codpro} · {p.unidade}
                    {Number(p.venda_minima) > 1 && ` · múltiplo de ${numero(p.venda_minima)}`}
                  </div>
                </div>
                <div className="text-end">
                  <div className="fw-bold">{dinheiro(p.preco_venda)}</div>
                  <div className="erp-doc__resultado-sub">
                    {naFilial > 0 ? `${numero(naFilial)} aqui`
                      : total > 0 ? 'só em outra loja' : 'sem estoque'}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {termo.trim().length >= 2 && !buscando && resultados.length === 0 && (
        <div className="erp-doc__resultado-sub mt-1">Nenhum produto encontrado para "{termo}".</div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Uma linha de produto. Em leitura e texto; em edicao os campos de decisao
// viram campo, no lugar exato onde eles aparecem no papel.
// ---------------------------------------------------------------------------
function LinhaItem({ item, edicao, apoio, aberto, aoAbrir, aoAmpliar }) {
  const erros = item.erros || [];
  const avisos = item.avisos || [];
  const derivados = item.derivados;
  const tipo = item.tipo_entrega || 'C';
  const exigePrevisao = tipo === 'E' || tipo === 'F';
  const opcoes = item.opcoes_atendimento || [];
  const opcaoAtual = opcoes.find(
    (o) => (o.filial_retirada || null) === (item.filial_retirada || null)
        && Number(o.faturamento_direto || 0) === Number(item.faturamento_direto || 0)
  );
  // O QUE PRECISA DE DECISÃO NÃO FICA ESCONDIDO ATRÁS DE UM CLIQUE.
  //
  // Abre sozinho quando há erro, quando o tipo exige previsão (Expedição e
  // Futura sempre exigem) ou quando há mais de uma filial capaz de atender —
  // que é justamente a escolha entre transferir e sair direto de lá. Deixar
  // isso colapsado faz o vendedor prometer sem saber que podia escolher.
  //
  // `aberto` é tri-estado de propósito: undefined = decide sozinho,
  // true/false = o vendedor mandou, e a vontade dele prevalece.
  const decisaoPendente = erros.length > 0 || exigePrevisao || opcoes.length > 1;
  const expandido = aberto === undefined ? decisaoPendente : aberto;
  const alterar = (mudancas) => edicao.onItem(item.chave, mudancas);

  return (
    <>
      <tr className={erros.length ? 'erp-doc__linha-erro' : ''}>
        <td>{item.sequencia}</td>
        <td className="erp-doc__foto-cel">
          {item.foto
            ? <img src={item.foto} alt="" loading="lazy"
                   className="erp-doc__foto erp-doc__foto--clicavel"
                   title="Ver a foto ampliada"
                   onClick={() => aoAmpliar({ url: item.foto, descricao: item.descricao, codpro: item.codpro })}
                   onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
            : <span className="erp-doc__foto erp-doc__foto--vazia"><i className="bi bi-box" /></span>}
        </td>
        <td>{item.codpro}</td>
        <td style={{ textAlign: 'right' }}>
          {edicao ? (
            <input type="number" className="erp-doc__inp erp-doc__inp--num" min="0" step="any"
                   value={item.quantidade} onFocus={selecionarTudo}
                   onChange={(e) => alterar({ quantidade: e.target.value })} />
          ) : qtd(item.quantidade)}
        </td>
        <td>{item.unidade}</td>
        <td>
          {item.descricao}
          {/* Previsão só em 'E' e 'F': em 'C' o cliente está levando agora,
              e a data de hoje repetida em toda linha vira ruído. */}
          {item.dt_prev_ent && tipo !== 'C' && (
            <div className="erp-doc__item-sub">
              Previsão: {data(item.dt_prev_ent)}
              {item.turno ? ` · ${item.turno}` : ''}
              {item.ponto_retirada ? ` · retira em ${item.ponto_retirada}` : ''}
            </div>
          )}
          {item.aguarda_transferencia === 1 && (
            <span className="erp-doc__badge-faturado" style={{ background: '#b45309' }}>
              AGUARDA TRANSFERÊNCIA · FILIAL {item.filial_estoque}
            </span>
          )}
          {item.sem_saldo_confirmado === 1 && (
            <span className="erp-doc__badge-faturado" style={{ background: '#b91c1c' }}>ENCOMENDA</span>
          )}
          {/* Fechar o painel esconde o detalhe, NÃO a pendência. Item que trava
              a venda tem que se anunciar na linha, senão o vendedor fecha, se
              esquece, e só descobre no botão de confirmar — que não explica
              qual item é. */}
          {edicao && !expandido && (erros.length > 0 || avisos.length > 0) && (
            <div className={`erp-doc__item-pendencia ${erros.length ? 'e-erro' : 'e-aviso'}`}
                 onClick={() => aoAbrir(item.chave, true)} role="button"
                 title="Abrir para resolver">
              <i className={`bi ${erros.length ? 'bi-x-circle-fill' : 'bi-exclamation-triangle-fill'}`} />
              <span>{(erros[0] || avisos[0])}</span>
              {(erros.length + avisos.length) > 1 && (
                <span className="erp-doc__item-pendencia-mais">
                  +{erros.length + avisos.length - 1}
                </span>
              )}
            </div>
          )}
        </td>
        <td style={{ textAlign: 'right' }}>
          {edicao ? (
            <input type="number" className="erp-doc__inp erp-doc__inp--num" min="0" step="0.01"
                   value={item.preco_unitario} onFocus={selecionarTudo}
                   onChange={(e) => alterar({ preco_unitario: e.target.value })} />
          ) : dinheiro(item.preco_unitario)}
        </td>
        <td>
          {edicao ? (
            <select className="erp-doc__inp erp-doc__inp--sel" value={tipo}
                    onChange={(e) => alterar({
                      tipo_entrega: e.target.value,
                      dt_prev_ent: item.dt_prev_ent || edicao.hoje,
                      // Caixa só sai da loja da venda.
                      ...(e.target.value === 'C' ? { filial_retirada: null, faturamento_direto: 0 } : {}),
                    })}>
              {(apoio?.tipos_entrega || []).map((t) => (
                <option key={t.codigo} value={t.codigo}>{t.codigo} — {t.nome}</option>
              ))}
            </select>
          ) : tipo}
        </td>
        <td style={{ textAlign: 'right' }}>{dinheiro(item.valor_liquido)}</td>
        {edicao && (
          <td className="erp-doc__acoes nao-imprimir">
            <button type="button" title={expandido ? 'Menos opções' : 'Mais opções deste item'}
                    onClick={() => aoAbrir(item.chave, !expandido)}>
              <i className={`bi bi-chevron-${expandido ? 'up' : 'down'}`} />
            </button>
            <button type="button" title="Remover item" className="erp-doc__acao-remover"
                    onClick={() => edicao.onRemoverItem(item.chave)}>
              <i className="bi bi-trash" />
            </button>
          </td>
        )}
      </tr>

      {edicao && expandido && (
        <tr className="erp-doc__painel nao-imprimir">
          <td colSpan={10}>
            <div className="erp-doc__painel-campos">
              {/* De onde sai a mercadoria — só quando há escolha a fazer. */}
              {opcoes.length > 1 && (
                <label className="erp-doc__painel-larga">
                  <span>
                    De onde sai a mercadoria
                    <span className="erp-doc__ficha-dica">
                      transferir para cá, ou {tipo === 'F' ? 'entregar' : 'o cliente pegar'} direto de lá
                    </span>
                  </span>
                  {/* ⚠️ Sem a opção vazia o campo MENTE. Quando o que está no
                      item não existe mais na lista — a filial da venda ficou
                      sem saldo, a quantidade subiu — `indexOf` devolve -1 e o
                      select exibe a PRIMEIRA opção, como se ela estivesse
                      escolhida. O vendedor lê "entregamos da filial 01" e o
                      erro logo abaixo fala da 04. Aqui ninguém escolhe pelo
                      vendedor: o campo diz que falta escolher. */}
                  <select className="erp-doc__inp"
                          value={opcaoAtual ? opcoes.indexOf(opcaoAtual) : ''}
                          onChange={(e) => {
                            const o = opcoes[Number(e.target.value)];
                            if (o) alterar({ filial_retirada: o.filial_retirada, faturamento_direto: o.faturamento_direto });
                          }}>
                    {!opcaoAtual && <option value="">Escolha de onde sai a mercadoria…</option>}
                    {opcoes.map((o, i) => (
                      <option key={i} value={i}>
                        {o.rotulo} — {numero(o.disponivel)} disponível
                        {o.aguarda_transferencia ? ' (aguarda transferência)' : ''}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {exigePrevisao && (
                <label>
                  <span>{tipo === 'E' ? 'Retira em' : 'Entregamos em'}</span>
                  <input type="date" className="erp-doc__inp" value={item.dt_prev_ent || ''}
                         onChange={(e) => alterar({ dt_prev_ent: e.target.value })} />
                </label>
              )}
              <label>
                <span>Desconto unitário (R$)</span>
                <input type="number" className="erp-doc__inp" min="0" step="0.01"
                       value={item.desconto_unitario ?? ''} onFocus={selecionarTudo}
                       onChange={(e) => alterar({ desconto_unitario: e.target.value })} />
              </label>
              <label className="erp-doc__painel-larga">
                <span>Observação do item</span>
                <input className="erp-doc__inp" maxLength={500} value={item.observacao || ''}
                       onChange={(e) => alterar({ observacao: e.target.value })} />
              </label>
            </div>

            {/* Com erro na linha, a explicação de onde sai a mercadoria contradiz o
                erro ("Disponível na sua loja" em cima de "Sem saldo na filial 04"). */}
            {derivados && !erros.length && (
              <div className="erp-doc__item-sub mt-1">
                <i className="bi bi-geo-alt me-1" />{derivados.explicacao}
                {derivados.filial_estoque !== derivados.filial_operacional && (
                  <> · sai da filial <strong>{derivados.filial_estoque}</strong>, atendida pela <strong>{derivados.filial_operacional}</strong></>
                )}
              </div>
            )}

            {erros.map((e, i) => (
              <div key={i} className="erp-doc__msg erp-doc__msg--erro">
                <i className="bi bi-x-circle-fill" /><span>{e}</span>
              </div>
            ))}
            {avisos.map((a, i) => (
              <div key={i} className="erp-doc__msg erp-doc__msg--aviso">
                <i className="bi bi-exclamation-triangle-fill" /><span>{a}</span>
              </div>
            ))}

            {/* Venda sem saldo: nunca em silêncio. O vendedor assume, e fica gravado. */}
            {erros.some((e) => e.includes('encomenda')) && (
              <label className="erp-doc__check">
                <input type="checkbox" checked={!!item.sem_saldo_confirmado}
                       onChange={(e) => alterar({ sem_saldo_confirmado: e.target.checked })} />
                <span>Confirmo que esta venda é por <strong>encomenda</strong> — o prazo depende do fornecedor.</span>
              </label>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
export default function DocumentoOrcamento({ pedido, itens, previa = false, edicao = null }) {
  const estado = estadoDoDocumento(pedido, previa);
  const apoio = edicao?.apoio;
  // Vontade EXPLÍCITA do vendedor por item (chave -> true|false). Sem chave, a
  // linha decide sozinha se abre — ver `decisaoPendente` em LinhaItem.
  const [abertura, setAbertura] = useState({});
  const definirAbertura = (chave, valor) => setAbertura((a) => ({ ...a, [chave]: valor }));
  const [fotoLupa, setFotoLupa] = useState(null);
  // Histórico de compra do cliente. Só existe para quem já é cliente do ERP:
  // rascunho de cadastro ainda não comprou nada, por definição.
  const [verHistorico, setVerHistorico] = useState(false);
  const colunas = edicao ? 10 : 9;   // usada pela linha de adicionar produto

  return (
    <div className="erp-doc">
      {fotoLupa && <FotoLupa foto={fotoLupa} onFechar={() => setFotoLupa(null)} />}
      {verHistorico && pedido.codclie && (
        <HistoricoCliente cliente={{ codclie: pedido.codclie, razao: pedido.cliente_razao }}
                          aoFechar={() => setVerHistorico(false)} />
      )}

      <header className="erp-doc__head">
        <img src={logo} alt="Centrão" className="erp-doc__logo" />
        <div className="erp-doc__title">
          <h2>{estado.titulo}</h2>
          <div className="erp-doc__alerta">
            NÃO É VÁLIDO COMO DOCUMENTO FISCAL E COMO GARANTIA DE VENDA
          </div>
          <div className="erp-doc__meta">Data do Pedido: {data(pedido.criado_em)}</div>
          <div className="erp-doc__meta erp-doc__meta--situacao">
            Situação: <strong>{estado.situacao}</strong>
          </div>
          {estado.aviso && (
            <div className="erp-doc__meta" style={{ color: '#b45309' }}>{estado.aviso}</div>
          )}
        </div>
      </header>

      <div className="erp-doc__filial">
        <div>
          <strong>FILIAL:</strong>{' '}
          {edicao ? (
            <select className="erp-doc__inp erp-doc__inp--sel" value={edicao.filial}
                    onChange={(e) => edicao.onFilial(e.target.value)}>
              {(apoio?.filiais || []).map((f) => (
                <option key={f.filial} value={f.filial}>{f.filial} — {f.nome}</option>
              ))}
            </select>
          ) : (
            <>{pedido.filial_nome || pedido.filial_venda}{pedido.filial_venda ? ` · CÓD.: ${pedido.filial_venda}` : ''}</>
          )}
        </div>
        <div>
          <strong>VENDEDOR:</strong> {pedido.vendedor_nome || pedido.codvend || '—'}
          {/* Vendedor não se digita: ele se prova com a senha. */}
          {edicao && (
            <button type="button" className="erp-doc__link nao-imprimir" onClick={edicao.onTrocarVendedor}>
              trocar
            </button>
          )}
          {pedido.criado_por_nome ? `  ·  LANÇADO POR: ${pedido.criado_por_nome}` : ''}
        </div>
      </div>

      <SecaoBar titulo="CLIENTE" acao={edicao && pedido.cliente_razao && !edicao.ficha && (
        <>
          {/* Fica ao lado do cliente porque é aqui que a pergunta nasce — e a
              resposta muda o preço que se oferece. O nome é o que a loja já
              usa: "histórico de compra", não invenção nossa. */}
          {pedido.codclie && (
            <button type="button" className="erp-doc__link" onClick={() => setVerHistorico(true)}>
              histórico de compra
            </button>
          )}
          {edicao.podeEditarCliente && (
            <button type="button" className="erp-doc__link" onClick={edicao.onCorrigirCliente}>
              adicionar endereço
            </button>
          )}
          <button type="button" className="erp-doc__link" onClick={() => edicao.onCliente(null)}>trocar</button>
        </>
      )} />
      <div className="erp-doc__bloco">
        {/* `key` de proposito: quando o CPF revela um cliente que ja existe, a
            ficha e remontada com os dados dele preenchidos. Sem a key, o React
            reaproveitaria o estado e os campos ficariam em branco. */}
        {edicao?.ficha ? (
          <FichaCliente key={edicao.ficha.cliente?.codclie || 'novo'}
                        cliente={edicao.ficha.cliente} salvando={edicao.ficha.salvando}
                        erro={edicao.ficha.erro} aoSalvar={edicao.ficha.aoSalvar}
                        aoEncontrar={edicao.ficha.aoEncontrar}
                        aoCancelar={edicao.ficha.aoCancelar} />
        ) : edicao && !pedido.cliente_razao ? (
          <>
            <BuscaCliente aoEscolher={edicao.onCliente} />
            <div className="erp-doc__ficha-rodape">
              <span className="erp-doc__resultado-sub">
                Não achou? O cadastro pode ser feito aqui, sem sair do orçamento.
              </span>
              <button type="button" className="btn btn-sm btn-outline-fnx" onClick={edicao.onNovoCliente}>
                <i className="bi bi-person-plus me-1" />Cadastrar cliente
              </button>
            </div>
          </>
        ) : (
          <>
            <div>
              <strong>CLIENTE:</strong> {pedido.cliente_razao || '—'}
              {/* Enquanto o Microuniverso não confirma, quem olha precisa saber
                  que o dado na tela ainda não é o que o ERP tem. */}
              {pedido.cliente_pendencia && (
                <span className="erp-doc__badge-faturado" style={{ background: '#b45309' }}>
                  AGUARDANDO O MICROUNIVERSO
                </span>
              )}
            </div>
            <div className="erp-doc__row2">
              <div><strong>CNPJ/CPF:</strong> {docFormatado(pedido.cliente_cpf_cnpj)}</div>
              <div><strong>CÓD.:</strong> {pedido.codclie || (edicao ? 'cadastro novo' : '—')}</div>
            </div>
          </>
        )}
      </div>

      {(!edicao || (pedido.cliente_razao && !edicao.ficha)) && (
        <>
          <SecaoBar titulo="ENDEREÇO" />
          <div className="erp-doc__bloco">
            <div><strong>ENDEREÇO:</strong> {pedido.cliente_endereco || '—'}</div>
            <div className="erp-doc__row2">
              <div><strong>BAIRRO:</strong> {pedido.cliente_bairro || '—'}</div>
              <div><strong>CEP:</strong> {cepFormatado(pedido.cliente_cep)}</div>
            </div>
            <div>
              <strong>CIDADE:</strong> {pedido.cliente_cidade || '—'}
              {pedido.cliente_uf ? ` - ${pedido.cliente_uf}` : ''}
            </div>
            {/* O endereço é do CADASTRO, não do pedido — mas o item 'F' depende
                dele. Quando falta, o aviso aparece aqui, onde o dado falta. */}
            {edicao && !pedido.cliente_endereco && itens.some((i) => i.tipo_entrega === 'F') && (
              <div className="erp-doc__msg erp-doc__msg--erro nao-imprimir">
                <i className="bi bi-x-circle-fill" />
                <span>
                  Para entregarmos, o cadastro do cliente precisa ter endereço.
                  {edicao.podeEditarCliente && (
                    <button type="button" className="erp-doc__link" onClick={edicao.onCorrigirCliente}>
                      completar agora
                    </button>
                  )}
                </span>
              </div>
            )}
          </div>
        </>
      )}

      <SecaoBar titulo="PRODUTOS" />
      <table className="erp-doc__tabela">
        <thead>
          <tr>
            <th>ITEM</th>
            <th>FOTO</th>
            <th>CÓD.</th>
            <th style={{ textAlign: 'right' }}>QTDE</th>
            <th>UN</th>
            <th>DESCRIÇÃO</th>
            <th style={{ textAlign: 'right' }}>VL. UNIT.</th>
            <th>TIPO SAÍDA</th>
            <th style={{ textAlign: 'right' }}>SUBTOTAL</th>
            {edicao && <th className="nao-imprimir" />}
          </tr>
        </thead>
        <tbody>
          {itens.map((it) => (
            <LinhaItem key={it.chave || it.sequencia} item={it} edicao={edicao} apoio={apoio}
                       aberto={abertura[it.chave]} aoAbrir={definirAbertura} aoAmpliar={setFotoLupa} />
          ))}
          {edicao && (
            <tr className="erp-doc__linha-adicionar nao-imprimir">
              <td colSpan={colunas}>
                <BuscaProduto filial={edicao.filial} aoEscolher={edicao.onAdicionarProduto}
                              aoMedirEstoque={edicao.aoMedirEstoque} aoAmpliar={setFotoLupa} />
              </td>
            </tr>
          )}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={6} />
            <td colSpan={2} style={{ textAlign: 'right' }}><strong>MERCADORIAS</strong></td>
            <td style={{ textAlign: 'right' }}>{dinheiro(pedido.valor_bruto)}</td>
            {edicao && <td className="nao-imprimir" />}
          </tr>
          {(edicao || Number(pedido.desconto_cabecalho) > 0) && (
            <tr>
              <td colSpan={6} />
              <td colSpan={2} style={{ textAlign: 'right' }}><strong>DESCONTO</strong></td>
              <td style={{ textAlign: 'right' }}>
                {edicao ? (
                  <input type="number" className="erp-doc__inp erp-doc__inp--num" min="0" step="0.01"
                         value={edicao.descontoCabecalho ?? ''} onFocus={selecionarTudo}
                         onChange={(e) => edicao.onDesconto(e.target.value)} />
                ) : dinheiro(pedido.desconto_cabecalho)}
              </td>
              {edicao && <td className="nao-imprimir" />}
            </tr>
          )}
          <tr>
            <td colSpan={6} />
            <td colSpan={2} style={{ textAlign: 'right' }}><strong>TOTAL</strong></td>
            <td style={{ textAlign: 'right' }}><strong>{dinheiro(pedido.valor_liquido)}</strong></td>
            {edicao && <td className="nao-imprimir" />}
          </tr>
        </tfoot>
      </table>

      <SecaoBar titulo="PAGAMENTO" />
      <div className="erp-doc__bloco">
        <div className="erp-doc__row3">
          {/* Forma de pagamento e tipo de documento são as DUAS escolhas que o
              vendedor já faz no Microuniverso, e as duas vêm de lá: "A VISTA",
              "NOTINHA 30", "PIX" são os termos da casa. A lista está na ordem
              de uso — o que ele mais escolhe fica no topo. */}
          <div>
            <strong>FORMA DE PAGAMENTO:</strong>{' '}
            {edicao ? (
              <select className="erp-doc__inp erp-doc__inp--sel" value={edicao.plano}
                      onChange={(e) => edicao.onPlano(e.target.value)}>
                {(apoio?.planos || []).map((p) => <option key={p.codigo} value={p.codigo}>{p.nome}</option>)}
              </select>
            ) : (pedido.plano_pagamento_nome || nomeDe(apoio?.planos, pedido.plano_pagamento) || '—')}
          </div>
          <div>
            <strong>TIPO DE DOCUMENTO:</strong>{' '}
            {edicao ? (
              <select className="erp-doc__inp erp-doc__inp--sel" value={edicao.tipoDocumento || ''}
                      onChange={(e) => edicao.onTipoDocumento(e.target.value)}>
                {/* Vazio de propósito: enquanto ninguém escolheu, o campo tem de
                    dizer que falta escolher, em vez de fingir que já escolheu a
                    primeira opção da lista. */}
                <option value="">— escolha —</option>
                {(apoio?.tipos_documento || []).map((d) => (
                  <option key={d.codigo} value={d.codigo}>{d.nome}</option>
                ))}
              </select>
            ) : (pedido.tipo_documento_nome || nomeDe(apoio?.tipos_documento, pedido.tipo_documento) || '—')}
          </div>
          <div>
            <strong>TIPO DE PEDIDO:</strong>{' '}
            {edicao ? (
              <select className="erp-doc__inp erp-doc__inp--sel" value={edicao.tpo}
                      onChange={(e) => edicao.onTpo(e.target.value)}>
                {(apoio?.tpos || []).map((t) => <option key={t.codigo} value={t.codigo}>{t.nome}</option>)}
              </select>
            ) : (pedido.tpo_nome || nomeDe(apoio?.tpos, pedido.tpo) || '—')}
          </div>
        </div>
      </div>

      {(edicao || pedido.observacao) && (
        <>
          <SecaoBar titulo="OBSERVAÇÃO" />
          <div className="erp-doc__bloco">
            {edicao ? (
              <input className="erp-doc__inp erp-doc__inp--larga" maxLength={500}
                     placeholder="Observação do orçamento"
                     value={edicao.observacao} onChange={(e) => edicao.onObservacao(e.target.value)} />
            ) : pedido.observacao}
          </div>
        </>
      )}

      {/* Os nomes são os do Microuniverso. O apelido entre parênteses é para
          quem ainda não fixou a letra — some da cabeça sozinho com o uso. */}
      <div className="erp-doc__meta" style={{ marginTop: 14, textAlign: 'center' }}>
        Tipo de saída: <strong>C</strong> Caixa (cliente leva) ·{' '}
        <strong>E</strong> Expedição (retira na loja) ·{' '}
        <strong>F</strong> Futura (nós entregamos)
      </div>
    </div>
  );
}
